import { useState, type FormEvent, type ChangeEvent } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Box, Button, TextField, Typography, Paper, InputAdornment, IconButton, Divider, Link } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TvIcon from '@mui/icons-material/Tv';
import { useSnackbar } from 'notistack';
import { authService } from '../api/auth';
import { removeAuthToken, setAuthToken } from '../api/client';
import { isValidEmail } from '../utils/validation';
import { Logo } from '../assets/logo';

export const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [tokenError, setTokenError] = useState('');
    const [touched, setTouched] = useState({ email: false, password: false });

    const getRedirectTarget = () => (location.state as any)?.from || '/dashboard';

    const validateEmail = (value: string) => {
        if (!value) {
            return t('login.validation.email.required');
        }
        if (!isValidEmail(value)) {
            return t('login.validation.email.invalid');
        }
        return '';
    };

    const validatePassword = (value: string) => {
        if (!value) {
            return t('login.validation.password.required');
        }
        return '';
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        if (touched.email) {
            setEmailError(validateEmail(value));
        }
    };

    const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassword(value);
        if (touched.password) {
            setPasswordError(validatePassword(value));
        }
    };

    const handleEmailBlur = () => {
        setTouched({ ...touched, email: true });
        setEmailError(validateEmail(email));
    };

    const handlePasswordBlur = () => {
        setTouched({ ...touched, password: true });
        setPasswordError(validatePassword(password));
    };

    const handleTokenChange = (e: ChangeEvent<HTMLInputElement>) => {
        setAccessToken(e.target.value);
        if (tokenError) {
            setTokenError('');
        }
    };

    const loginMutation = useMutation({
        mutationFn: authService.login,
        onSuccess: (accessToken) => {
            setAuthToken(accessToken);
            enqueueSnackbar(t('login.success'), { variant: 'success' });
            navigate(getRedirectTarget(), { replace: true });
        },
        onError: () => {
            enqueueSnackbar(t('login.error'), { variant: 'error' });
        }
    });

    const tokenLoginMutation = useMutation({
        mutationFn: async (token: string) => {
            setAuthToken(token);
            try {
                await authService.getMe();
                return token;
            } catch (error) {
                removeAuthToken();
                throw error;
            }
        },
        onSuccess: () => {
            enqueueSnackbar(t('login.success'), { variant: 'success' });
            navigate(getRedirectTarget(), { replace: true });
        },
        onError: () => {
            setTokenError(t('login.token.invalid', { defaultValue: 'Token is invalid or expired' }));
            enqueueSnackbar(t('login.error'), { variant: 'error' });
        },
    });

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const emailValidationError = validateEmail(email);
        const passwordValidationError = validatePassword(password);
        
        setEmailError(emailValidationError);
        setPasswordError(passwordValidationError);
        setTouched({ email: true, password: true });

        if (!emailValidationError && !passwordValidationError) {
            loginMutation.mutate({ email, password });
        }
    };

    const handleTokenSubmit = () => {
        const token = accessToken.trim();
        if (!token) {
            setTokenError(t('validation.required'));
            return;
        }

        setTokenError('');
        tokenLoginMutation.mutate(token);
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default'
            }}
        >
            <Paper 
                elevation={0}
                sx={{ 
                    p: 4,
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: 3,
                    bgcolor: 'background.paper'
                }}
            >
                <Logo 
                    sx={{ 
                        width: 48,
                        height: 48,
                        mb: 2,
                        color: 'text.primary',
                        display: 'block',
                        margin: '0 auto'
                    }} 
                />
                <Typography 
                    component="h1" 
                    variant="h4" 
                    gutterBottom 
                    align="center"
                    sx={{ 
                        mb: 4,
                        fontWeight: 600
                    }}
                >
                    {t('login.title')}
                </Typography>
                <Box component="form" onSubmit={handleSubmit}>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 500
                        }}
                    >
                        {t('login.email')}
                    </Typography>
                    <TextField
                        fullWidth
                        id="email"
                        name="email"
                        placeholder="hello@gmail.com"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        error={!!emailError}
                        helperText={emailError}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 500
                        }}
                    >
                        {t('login.password')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={handlePasswordChange}
                        onBlur={handlePasswordBlur}
                        error={!!passwordError}
                        helperText={passwordError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleTogglePasswordVisibility}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loginMutation.isPending || tokenLoginMutation.isPending}
                        sx={{
                            mt: 2,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            bgcolor: 'text.primary',
                            '&:hover': {
                                bgcolor: 'text.primary',
                                opacity: 0.9
                            }
                        }}
                    >
                        {loginMutation.isPending ? t('common.loading') : t('login.title')}
                    </Button>

                    <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('common.or', { defaultValue: 'OR' })}
                        </Typography>
                    </Divider>

                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 500
                        }}
                    >
                        {t('login.token.label', { defaultValue: 'Access Token' })}
                    </Typography>
                    <TextField
                        fullWidth
                        name="access-token"
                        placeholder={t('login.token.placeholder', { defaultValue: 'Paste your API token here' })}
                        value={accessToken}
                        onChange={handleTokenChange}
                        error={!!tokenError}
                        helperText={tokenError}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                    <Button
                        type="button"
                        fullWidth
                        variant="outlined"
                        disabled={loginMutation.isPending || tokenLoginMutation.isPending}
                        onClick={handleTokenSubmit}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                        }}
                    >
                        {tokenLoginMutation.isPending
                            ? t('common.loading')
                            : t('login.token.submit', { defaultValue: 'Sign In with Token' })}
                    </Button>

                    <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('common.or', { defaultValue: 'OR' })}
                        </Typography>
                    </Divider>

                    <Button
                        component={RouterLink}
                        to="/device-login"
                        fullWidth
                        variant="text"
                        startIcon={<TvIcon />}
                        sx={{
                            py: 1.25,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {t('login.deviceLogin')}
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 1 }}>
                        {t('login.deviceLoginHint')}
                    </Typography>
                    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                        <Link component={RouterLink} to="/link" underline="hover">
                            {t('login.approveDevice')}
                        </Link>
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}; 
